
import React from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/contexts/LanguageContext";
import { ArrowRight, Star, Users, Shield, Timer } from "lucide-react";
import { AspectRatio } from "@/components/ui/aspect-ratio";

const New2Hero = () => {
  const { language } = useLanguage();
  
  const content = {
    fr: {
      badge: "✨ Plus de 1000 familles nous font confiance",
      headline: "Le cadeau qui grandit avec votre enfant",
      subheadline: "Plus qu'un simple cadeau : une expérience émotionnelle unique qui accompagne votre enfant jusqu'à ses moments les plus importants",
      description: "Combinez messages du cœur, souvenirs précieux et contribution financière dans une capsule temporelle sécurisée. Offrez-lui votre présence pour ses 18 ans, son diplôme, ou son premier appartement.",
      cta: "Créer sa capsule temporelle",
      watchDemo: "Voir comment ça marche",
      socialProof: {
        families: "1000+",
        familiesLabel: "Familles",
        invested: "15M€",
        investedLabel: "Investis",
        rating: "4.9/5",
        ratingLabel: "Satisfaction"
      },
      urgency: "🎁 Offre de lancement : -30% jusqu'au 31 décembre",
      riskFree: "30 jours satisfait ou remboursé"
    },
    en: {
      badge: "✨ Trusted by 1000+ families",
      headline: "The gift that grows with your child",
      subheadline: "More than just a gift: a unique emotional experience that accompanies your child to their most important moments",
      description: "Combine heartfelt messages, precious memories, and financial contribution in a secure time capsule. Give them your presence for their 18th birthday, graduation, or first apartment.",
      cta: "Create their time capsule",
      watchDemo: "See how it works",
      socialProof: {
        families: "1000+",
        familiesLabel: "Families",
        invested: "€15M",
        investedLabel: "Invested",
        rating: "4.9/5",
        ratingLabel: "Satisfaction"
      },
      urgency: "🎁 Launch offer: -30% until December 31st",
      riskFree: "30-day money-back guarantee"
    },
    de: {
      badge: "✨ Vertrauen von 1000+ Familien",
      headline: "Das Geschenk, das mit Ihrem Kind wächst",
      subheadline: "Mehr als nur ein Geschenk: eine einzigartige emotionale Erfahrung, die Ihr Kind zu seinen wichtigsten Momenten begleitet",
      description: "Kombinieren Sie herzliche Botschaften, kostbare Erinnerungen und finanzielle Beiträge in einer sicheren Zeitkapsel. Schenken Sie ihnen Ihre Anwesenheit zum 18. Geburtstag, Abschluss oder zur ersten Wohnung.",
      cta: "Zeitkapsel erstellen",
      watchDemo: "Sehen Sie, wie es funktioniert",
      socialProof: {
        families: "1000+",
        familiesLabel: "Familien",
        invested: "15M€",
        investedLabel: "Investiert",
        rating: "4.9/5",
        ratingLabel: "Zufriedenheit"
      },
      urgency: "🎁 Launch-Angebot: -30% bis 31. Dezember",
      riskFree: "30 Tage Geld-zurück-Garantie"
    }
  };

  return (
    <section className="pt-24 pb-16 bg-gradient-to-br from-legatia-light via-white to-legatia-orange/5 relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute top-20 right-[15%] w-72 h-72 bg-gradient-to-br from-legatia-orange/20 to-legatia-yellow/20 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 left-[10%] w-64 h-64 bg-gradient-to-tr from-legatia-green/15 to-legatia-blue/15 rounded-full blur-2xl"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        {/* Top badge */}
        <div className="text-center mb-8">
          <Badge variant="secondary" className="bg-legatia-orange/10 text-legatia-orange border-legatia-orange/20 px-4 py-2 text-sm font-medium">
            {content[language].badge}
          </Badge>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
          {/* Left column - Content */}
          <div className="space-y-8">
            <div className="space-y-6">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
                <span className="gradient-text">{content[language].headline}</span>
              </h1>
              
              <p className="text-xl md:text-2xl text-gray-700 font-medium leading-relaxed">
                {content[language].subheadline}
              </p>
              
              <p className="text-lg text-gray-600 leading-relaxed max-w-xl">
                {content[language].description}
              </p>
            </div>

            {/* Urgency banner */}
            <div className="bg-gradient-to-r from-legatia-orange to-legatia-red text-white p-4 rounded-lg shadow-lg">
              <div className="flex items-center gap-2">
                <Timer className="h-5 w-5" />
                <span className="font-semibold">{content[language].urgency}</span>
              </div>
            </div>

            {/* CTA buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                size="lg"
                className="bg-gradient-to-r from-legatia-orange to-legatia-red hover:from-legatia-orange/90 hover:to-legatia-red/90 text-white px-8 py-6 text-lg rounded-full shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all"
                onClick={() => window.location.href = "https://app.getlegatia.ch/"}
              >
                {content[language].cta}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              
              <Button 
                variant="outline" 
                size="lg"
                className="border-2 border-legatia-orange text-legatia-orange hover:bg-legatia-orange hover:text-white px-8 py-6 text-lg rounded-full"
                onClick={() => {
                  document.getElementById("how-it-works")?.scrollIntoView({ behavior: "smooth" });
                }}
              >
                {content[language].watchDemo}
              </Button>
            </div>

            {/* Risk-free guarantee */}
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Shield className="h-4 w-4 text-legatia-green" />
              <span>{content[language].riskFree}</span>
            </div>

            {/* Social proof stats */}
            <div className="grid grid-cols-3 gap-6 pt-8 border-t border-gray-200">
              {[
                { number: content[language].socialProof.families, label: content[language].socialProof.familiesLabel, icon: Users },
                { number: content[language].socialProof.invested, label: content[language].socialProof.investedLabel, icon: ArrowRight },
                { number: content[language].socialProof.rating, label: content[language].socialProof.ratingLabel, icon: Star }
              ].map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="flex justify-center mb-2">
                    <stat.icon className="h-5 w-5 text-legatia-orange" />
                  </div>
                  <div className="text-2xl font-bold text-gray-900">{stat.number}</div>
                  <div className="text-sm text-gray-600">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right column - Hero image */}
          <div className="relative">
            <div className="relative z-10 rounded-2xl overflow-hidden shadow-2xl">
              <AspectRatio ratio={4/3}>
                <img 
                  src="/lovable-uploads/edb21411-962a-4c80-83dc-c58597759057.png" 
                  alt="Family creating emotional time capsule" 
                  className="w-full h-full object-cover"
                />
              </AspectRatio>
              
              {/* Overlay with play button */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent flex items-center justify-center">
                <Button
                  size="lg"
                  variant="secondary"
                  className="bg-white/90 hover:bg-white text-legatia-orange rounded-full px-8 py-4"
                  onClick={() => {
                    document.getElementById("how-it-works")?.scrollIntoView({ behavior: "smooth" });
                  }}
                >
                  ▶ {content[language].watchDemo}
                </Button>
              </div>
            </div>
            
            {/* Decorative elements */}
            <div className="absolute -bottom-8 -right-8 w-32 h-32 bg-legatia-yellow/30 rounded-full z-0 animate-float"></div>
            <div className="absolute -top-6 -left-6 w-24 h-24 bg-legatia-orange/20 rounded-full z-0 animate-float" style={{ animationDelay: "1.5s" }}></div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default New2Hero;
